import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    Plus, Phone, X, Search, MessageCircle,
    ChevronRight, User, MapPin,
    ArrowUpDown, Camera, Trash2, Edit2
} from 'lucide-react'
import { getCallLogs, createCallLog, updateCallLog, deleteCallLog, getCustomers, getUsers } from '../../services/staffServices'
import { uploadToStorage } from '../../lib/supabase'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { StaggerContainer, StaggerItem, ModalOverlay, ButtonLoader } from './components/StaffAnimations'

// ─── Inquiry Category Tree ─────────────────────────────────────────────────────
const INQUIRY_CATEGORIES = [
    {
        name: 'EV & Vehicles',
        sub: [
            'Dealer', 'Dealership', 'EV spare parts', 'EV charging station',
            'Electric loader', 'Motorvolt cycle', 'Hilman Axcel',
            'Mahindra', 'OLA', 'Ather', 'Ampere', 'ZELIO EV',
            'TVS iQube', 'Non-RTO EV', 'Normal EV', 'Chinese EV',
        ],
    },
    {
        name: 'Battery & Power',
        sub: [
            'Advantex battery', 'Toto battery', 'Inverter battery',
            'Home inverter', 'Solar', 'Battery charger',
        ],
    },
    {
        name: 'Service & Repair',
        sub: ['Service / Repair', 'Toto Service', 'Job Application'],
    },
    {
        name: 'Telecom & CCTV',
        sub: ['CCTV', 'BSNL', 'BSNL Service', 'BSNL New Connection'],
    },
    {
        name: 'General Inquiry',
        sub: ['Security enquiry', 'Other state call', 'Bill', 'Others'],
    },
]

// Brands that trigger the booking dialog
const BOOKING_BRANDS = ['Mahindra', 'OLA', 'Ather', 'Ampere']

const INTEREST_LEVELS = ['High', 'Very High', 'Medium', 'Low']
const INTEREST_COLORS = {
    'Very High': 'bg-blue-500/15 text-blue-400 border border-blue-500/20',
    'High': 'bg-green-500/15 text-green-400 border border-green-500/20',
    'Medium': 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
    'Low': 'bg-slate-500/15 text-slate-400 border border-slate-500/20',
}
const STATUS_COLORS = {
    NEW: 'bg-blue-500/15 text-blue-400',
    IN_PROGRESS: 'bg-amber-500/15 text-amber-400',
    RESOLVED: 'bg-green-500/15 text-green-400',
    CLOSED: 'bg-slate-500/15 text-slate-400',
    CONVERTED: 'bg-purple-500/15 text-purple-400',
    FOLLOW_UP_SCHEDULED: 'bg-orange-500/15 text-orange-400',
}
const PRIORITY_COLORS = {
    URGENT: 'bg-red-500/15 text-red-400',
    HIGH: 'bg-orange-500/15 text-orange-400',
    MEDIUM: 'bg-amber-500/15 text-amber-400',
    LOW: 'bg-slate-500/15 text-slate-400',
}

const AVATAR_COLORS = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-amber-600', 'bg-rose-600', 'bg-indigo-600']

function UserAvatar({ user, size = 7 }) {
    const colorIdx = user?.name ? user.name.charCodeAt(0) % AVATAR_COLORS.length : 0
    const px = size * 4
    if (user?.profile_photo_url) {
        return <img src={user.profile_photo_url} alt={user.name} className="rounded-full object-cover shrink-0" style={{ width: px, height: px }} />
    }
    return (
        <div className={`rounded-full ${AVATAR_COLORS[colorIdx]} flex items-center justify-center text-xs font-bold text-white shrink-0`} style={{ width: px, height: px }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
        </div>
    )
}

// ─── Photo upload button ───────────────────────────────────────────────────────
function PhotoUpload({ label, url, onUpload, uploading }) {
    const ref = useRef()
    return (
        <div>
            <label className="text-xs text-slate-400 block mb-1">{label}</label>
            <div
                onClick={() => ref.current?.click()}
                className="w-full h-16 bg-slate-800 border border-dashed border-white/15 rounded-xl flex items-center justify-center gap-2 cursor-pointer hover:border-blue-500/50 transition overflow-hidden"
            >
                {url ? (
                    <img src={url} alt={label} className="w-full h-full object-cover rounded-xl" />
                ) : uploading ? (
                    <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                    <>
                        <Camera size={14} className="text-slate-400" />
                        <span className="text-xs text-slate-400">Upload</span>
                    </>
                )}
            </div>
            <input ref={ref} type="file" accept="image/*" className="hidden"
                onChange={async e => {
                    const file = e.target.files[0]
                    if (file) await onUpload(file)
                    e.target.value = ''
                }}
            />
        </div>
    )
}

// ─── Mahindra/OLA/Ather/Ampere Booking Dialog ─────────────────────────────────
function BookingDialog({ brand, data, onChange, onClose }) {
    const prefix = brand.toLowerCase().replace(/ /g, '_')
    const set = (k, v) => onChange({ ...data, [`${prefix}_${k}`]: v })
    const get = (k) => data[`${prefix}_${k}`] || ''

    const [uploading, setUploading] = useState({})
    const handlePhotoUpload = async (field, file) => {
        setUploading(u => ({ ...u, [field]: true }))
        try {
            const url = await uploadToStorage('customer-documents', 'customers', file)
            set(field, url)
        } catch (e) {
            toast.error('Upload failed')
        } finally {
            setUploading(u => ({ ...u, [field]: false }))
        }
    }

    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/10 w-full max-w-md max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-white/5 sticky top-0 bg-slate-900">
                    <h3 className="font-semibold text-white text-sm">{brand} Booking Details</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-3">
                    {/* Booking Type */}
                    <div className="grid grid-cols-2 gap-2">
                        {['Query', 'Booking'].map(t => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => set('booking_type', t)}
                                className={`py-2 rounded-xl text-sm font-medium transition border ${get('booking_type') === t ? 'bg-blue-600 border-blue-500 text-white' : 'border-white/8 text-slate-400 hover:text-white'}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    {[
                        { key: 'payee_name', label: 'Payee Name' },
                        { key: 'nominee_name', label: 'Nominee Name' },
                        { key: 'aadhar_number', label: 'Aadhar Number' },
                        { key: 'model', label: 'Model' },
                        { key: 'color', label: 'Color' },
                        { key: 'booking_amount', label: 'Booking Amount', type: 'number' },
                        { key: 'party_loan_referral', label: 'Party / Loan Referral' },
                        { key: 'desk_staff_name', label: 'Desk Staff Name' },
                        { key: 'refer_to', label: 'Refer To' },
                    ].map(f => (
                        <div key={f.key}>
                            <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
                            <input
                                type={f.type || 'text'}
                                value={get(f.key)}
                                onChange={e => set(f.key, e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                        </div>
                    ))}

                    {/* License Status */}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">License Status</label>
                        <div className="flex gap-2">
                            {['Available', 'Not Available'].map(s => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => set('license_status', s)}
                                    className={`flex-1 py-2 rounded-xl text-xs font-medium transition border ${get('license_status') === s ? 'bg-blue-600 border-blue-500 text-white' : 'border-white/8 text-slate-400 hover:text-white'}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Payment Type */}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Payment Type</label>
                        <div className="flex gap-2">
                            {['Cash', 'Finance'].map(s => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => set('payment_type', s)}
                                    className={`flex-1 py-2 rounded-xl text-xs font-medium transition border ${get('payment_type') === s ? 'bg-blue-600 border-blue-500 text-white' : 'border-white/8 text-slate-400 hover:text-white'}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Mode of Payment</label>
                        <input
                            value={get('mode_of_payment')}
                            onChange={e => set('mode_of_payment', e.target.value)}
                            placeholder="UPI / Cash / Cheque..."
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    {/* Photo uploads */}
                    <div className="grid grid-cols-3 gap-2">
                        <PhotoUpload
                            label="Jio Tag Photo"
                            url={get('jio_tag_photo_url')}
                            onUpload={f => handlePhotoUpload('jio_tag_photo_url', f)}
                            uploading={uploading['jio_tag_photo_url']}
                        />
                        <PhotoUpload
                            label="Aadhar Photo"
                            url={get('aadhar_photo_url')}
                            onUpload={f => handlePhotoUpload('aadhar_photo_url', f)}
                            uploading={uploading['aadhar_photo_url']}
                        />
                        <PhotoUpload
                            label="License Photo"
                            url={get('license_photo_url')}
                            onUpload={f => handlePhotoUpload('license_photo_url', f)}
                            uploading={uploading['license_photo_url']}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Create / Edit Call Log Form ───────────────────────────────────────────────
const EMPTY_FORM = {
    caller_name: '',
    caller_number: '',
    caller_location: '',
    // Category
    category: '',
    subcategory: '',
    // Query details
    query_details: '',
    customer_interest_level: 'Medium',
    call_status: 'NEW',
    // Referral
    referred_to_staff_id: '',
    referred_to_staff_name: '',
    // Follow-up
    needs_follow_up: false,
    follow_up_date: '',
    priority: 'MEDIUM',
    follow_up_notes: '',
}

function CallLogModal({ log, users, customers, profile, onClose, onSave, isSaving }) {
    const isEdit = !!log?.id

    // Reverse-parse category/subcategory from business_type + query_details on edit
    const initCategory = () => {
        if (!isEdit) return ''
        return INQUIRY_CATEGORIES.find(c => c.name === log.business_type)?.name || ''
    }
    const initSubcategory = () => {
        if (!isEdit) return ''
        // Sub-category was encoded as first line of query_details: "Category: X\nSub-Category: Y\n\n..."
        const match = log.query_details?.match(/^Sub-Category: (.+)$/m)
        return match ? match[1] : ''
    }
    const initNotes = () => {
        if (!isEdit) return ''
        const lines = (log.query_details || '').split('\n')
        const noteStart = lines.findIndex(l => l === '')
        return noteStart >= 0 ? lines.slice(noteStart + 1).join('\n').trim() : log.query_details || ''
    }

    const [form, setForm] = useState({
        caller_name: log?.caller_name || '',
        caller_number: log?.caller_number || '',
        caller_location: log?.caller_location || '',
        category: initCategory(),
        subcategory: initSubcategory(),
        query_details: initNotes(),
        customer_interest_level: log?.customer_interest_level || 'Medium',
        call_status: log?.call_status || 'NEW',
        referred_to_staff_id: log?.referred_to_staff_id || '',
        referred_to_staff_name: log?.referred_to_staff_name || '',
        needs_follow_up: log?.needs_follow_up || false,
        follow_up_date: log?.follow_up_date || '',
        priority: log?.priority || 'MEDIUM',
        follow_up_notes: log?.follow_up_notes || '',
        // Booking fields carried over on edit
        ...Object.fromEntries(
            Object.entries(log || {}).filter(([k]) =>
                k.startsWith('mahindra_') || k.startsWith('ola_') ||
                k.startsWith('ather_') || k.startsWith('ampere_')
            )
        ),
    })
    const [bookingDialog, setBookingDialog] = useState(null) // brand string | null
    const [customerSearch, setCustomerSearch] = useState(log?.caller_name || '')
    const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false)
    const [expandedCat, setExpandedCat] = useState(form.category || null)

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const filteredCustomers = customers.filter(c =>
        c.customer_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.phone_number?.includes(customerSearch)
    ).slice(0, 6)

    const handleCustomerSelect = (c) => {
        set('caller_name', c.customer_name)
        set('caller_number', c.phone_number)
        setCustomerSearch(c.customer_name)
        setShowCustomerSuggestions(false)
    }

    const handleSubcategorySelect = (cat, sub) => {
        set('category', cat)
        set('subcategory', sub)
        if (BOOKING_BRANDS.includes(sub)) setBookingDialog(sub)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!form.caller_name.trim()) { toast.error('Caller name is required'); return }
        if (!form.caller_number.trim()) { toast.error('Phone number is required'); return }
        if (!form.category) { toast.error('Please select an inquiry category'); return }

        // Encode category + sub into query_details
        const encodedQuery = [
            `Category: ${form.category}`,
            `Sub-Category: ${form.subcategory}`,
            '',
            form.query_details,
        ].join('\n')

        const payload = {
            caller_name: form.caller_name,
            caller_number: form.caller_number,
            caller_location: form.caller_location,
            business_type: form.category,
            query_details: encodedQuery,
            customer_interest_level: form.customer_interest_level,
            call_status: form.call_status,
            referred_to_staff_id: form.referred_to_staff_id || null,
            referred_to_staff_name: form.referred_to_staff_name || null,
            needs_follow_up: form.needs_follow_up,
            follow_up_date: form.needs_follow_up ? form.follow_up_date : null,
            priority: form.needs_follow_up ? form.priority : form.priority,
            follow_up_notes: form.needs_follow_up ? form.follow_up_notes : null,
            logged_by: profile?.name || '',
            logged_by_id: profile?.id,
        }

        // Attach booking data if any brand sub was filled
        BOOKING_BRANDS.forEach(brand => {
            const prefix = brand.toLowerCase().replace(/ /g, '_')
            Object.entries(form).forEach(([k, v]) => {
                if (k.startsWith(`${prefix}_`)) payload[k] = v
            })
        })

        onSave(payload)
    }

    const currentCatSubs = INQUIRY_CATEGORIES.find(c => c.name === expandedCat)?.sub || []

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-lg max-h-[94vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900 z-10">
                    <h2 className="font-semibold text-white">{isEdit ? 'Edit Call Log' : 'Log a Call'}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition"><X size={18} /></button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-5">
                    {/* ── 1. Customer Information ── */}
                    <div>
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Customer Information</h3>
                        <div className="space-y-3">
                            {/* Customer name with autocomplete */}
                            <div className="relative">
                                <label className="text-xs text-slate-400 block mb-1">Customer Name *</label>
                                <div className="relative">
                                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        value={form.caller_name}
                                        onChange={e => {
                                            set('caller_name', e.target.value)
                                            setCustomerSearch(e.target.value)
                                            setShowCustomerSuggestions(true)
                                        }}
                                        onFocus={() => setShowCustomerSuggestions(true)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                        placeholder="Name or search existing..."
                                    />
                                </div>
                                {showCustomerSuggestions && customerSearch.length >= 1 && filteredCustomers.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 bg-slate-800 border border-white/10 rounded-xl mt-1 z-20 shadow-xl overflow-hidden">
                                        {filteredCustomers.map(c => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onMouseDown={() => handleCustomerSelect(c)}
                                                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 text-left"
                                            >
                                                <div className="w-7 h-7 rounded-full bg-blue-600/30 flex items-center justify-center text-xs font-bold text-blue-400 shrink-0">
                                                    {c.customer_name?.[0]?.toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="text-sm text-white">{c.customer_name}</p>
                                                    <p className="text-xs text-slate-400">{c.phone_number}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Phone Number *</label>
                                    <div className="relative">
                                        <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            value={form.caller_number}
                                            onChange={e => set('caller_number', e.target.value)}
                                            className="w-full bg-slate-800 border border-white/8 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                            placeholder="10-digit"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Location</label>
                                    <div className="relative">
                                        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            value={form.caller_location}
                                            onChange={e => set('caller_location', e.target.value)}
                                            className="w-full bg-slate-800 border border-white/8 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                            placeholder="Area / City"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── 2. Inquiry Category ── */}
                    <div>
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Inquiry Category *</h3>
                        <div className="space-y-1.5">
                            {INQUIRY_CATEGORIES.map(cat => (
                                <div key={cat.name} className="border border-white/8 rounded-xl overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setExpandedCat(expandedCat === cat.name ? null : cat.name)}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition ${form.category === cat.name ? 'bg-blue-600/20 text-blue-300' : 'text-slate-300 hover:bg-white/5'}`}
                                    >
                                        <span>{cat.name}</span>
                                        <ChevronRight
                                            size={15}
                                            className={`text-slate-500 transition-transform ${expandedCat === cat.name ? 'rotate-90' : ''}`}
                                        />
                                    </button>
                                    {expandedCat === cat.name && (
                                        <div className="border-t border-white/5 bg-slate-800/40 p-2 flex flex-wrap gap-1.5">
                                            {cat.sub.map(sub => (
                                                <button
                                                    key={sub}
                                                    type="button"
                                                    onClick={() => handleSubcategorySelect(cat.name, sub)}
                                                    className={`px-2.5 py-1 text-xs rounded-lg transition border ${form.category === cat.name && form.subcategory === sub
                                                        ? 'bg-blue-600 border-blue-500 text-white'
                                                        : 'border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                                                        }`}
                                                >
                                                    {sub}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {form.category && (
                            <div className="flex gap-2 mt-2 flex-wrap">
                                <span className="text-xs bg-blue-600/20 text-blue-300 px-2 py-1 rounded-lg">{form.category}</span>
                                {form.subcategory && (
                                    <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded-lg">{form.subcategory}</span>
                                )}
                                {BOOKING_BRANDS.includes(form.subcategory) && (
                                    <button
                                        type="button"
                                        onClick={() => setBookingDialog(form.subcategory)}
                                        className="text-xs bg-amber-500/15 text-amber-400 px-2 py-1 rounded-lg hover:bg-amber-500/25 transition"
                                    >
                                        Edit {form.subcategory} booking details →
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── 3. Query Details ── */}
                    <div>
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Query Details</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Notes</label>
                                <textarea
                                    value={form.query_details}
                                    onChange={e => set('query_details', e.target.value)}
                                    rows={3}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none"
                                    placeholder="Describe the customer's inquiry..."
                                />
                            </div>

                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Customer Interest Level</label>
                                <div className="flex gap-2 flex-wrap">
                                    {INTEREST_LEVELS.map(lvl => (
                                        <button
                                            key={lvl}
                                            type="button"
                                            onClick={() => set('customer_interest_level', lvl)}
                                            className={`px-3 py-1.5 text-xs rounded-xl font-medium transition border ${form.customer_interest_level === lvl
                                                ? 'bg-blue-600 border-blue-500 text-white'
                                                : 'border-white/8 text-slate-400 hover:text-white'
                                                }`}
                                        >
                                            {lvl}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Call Status</label>
                                <select
                                    value={form.call_status}
                                    onChange={e => set('call_status', e.target.value)}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                >
                                    {['NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'CONVERTED', 'FOLLOW_UP_SCHEDULED'].map(s => (
                                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* ── 4. Referral ── */}
                    <div>
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Referral</h3>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Refer To (Staff Member)</label>
                            <select
                                value={form.referred_to_staff_id}
                                onChange={e => {
                                    const staff = users.find(u => u.id === e.target.value)
                                    set('referred_to_staff_id', e.target.value)
                                    set('referred_to_staff_name', staff?.name || '')
                                }}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="">No referral</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* ── 5. Follow-up ── */}
                    <div>
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Follow-up</h3>
                        <div className="space-y-3">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <div
                                    onClick={() => set('needs_follow_up', !form.needs_follow_up)}
                                    className={`w-10 h-5 rounded-full transition relative ${form.needs_follow_up ? 'bg-blue-600' : 'bg-slate-700'}`}
                                >
                                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${form.needs_follow_up ? 'left-5' : 'left-0.5'}`} />
                                </div>
                                <span className="text-sm text-slate-300">Needs follow-up</span>
                            </label>

                            {form.needs_follow_up && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-slate-400 block mb-1">Follow-up Date</label>
                                        <input
                                            type="date"
                                            value={form.follow_up_date}
                                            onChange={e => set('follow_up_date', e.target.value)}
                                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-slate-400 block mb-1">Priority</label>
                                        <select
                                            value={form.priority}
                                            onChange={e => set('priority', e.target.value)}
                                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                        >
                                            {['URGENT', 'HIGH', 'MEDIUM', 'LOW'].map(p => (
                                                <option key={p} value={p}>{p}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-xs text-slate-400 block mb-1">Follow-up Notes</label>
                                        <textarea
                                            value={form.follow_up_notes}
                                            onChange={e => set('follow_up_notes', e.target.value)}
                                            rows={2}
                                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none"
                                            placeholder="What needs to be followed up on?"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving} className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-medium transition">
                            <ButtonLoader loading={isSaving}>{isEdit ? 'Update' : 'Save Log'}</ButtonLoader>
                        </button>
                    </div>
                </form>
            </div>

            {/* Booking dialog */}
            {bookingDialog && (
                <BookingDialog
                    brand={bookingDialog}
                    data={form}
                    onChange={updates => setForm(f => ({ ...f, ...updates }))}
                    onClose={() => setBookingDialog(null)}
                />
            )}
        </div>
    )
}

// ─── Parse sub-category from query_details ────────────────────────────────────
function parseSubcategory(queryDetails) {
    const match = (queryDetails || '').match(/^Sub-Category: (.+)$/m)
    return match ? match[1] : null
}
function parseNotes(queryDetails) {
    if (!queryDetails) return ''
    const lines = queryDetails.split('\n')
    const emptyIdx = lines.findIndex(l => l.trim() === '')
    return emptyIdx >= 0 ? lines.slice(emptyIdx + 1).join('\n').trim() : queryDetails
}

// ─── Call Log Card ─────────────────────────────────────────────────────────────
function CallLogCard({ log, users, onEdit, onDelete }) {
    const subcategory = parseSubcategory(log.query_details)
    const notes = parseNotes(log.query_details)
    const loggedByUser = users.find(u => u.id === log.logged_by_id)
    const referredUser = users.find(u => u.id === log.referred_to_staff_id)
    const hasBooking = BOOKING_BRANDS.some(b => {
        const prefix = b.toLowerCase().replace(/ /g, '_')
        return log[`${prefix}_booking_type`]
    })

    return (
        <div className="bg-slate-900 rounded-xl border border-white/5 hover:border-white/10 p-4 transition">
            <div className="flex items-start gap-3">
                {/* Left: Logged by avatar */}
                <div className="shrink-0 mt-0.5">
                    <UserAvatar user={loggedByUser} size={9} />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="font-semibold text-white">{log.caller_name}</p>
                            <p className="text-xs text-slate-400">{log.caller_number}{log.caller_location ? ` · ${log.caller_location}` : ''}</p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                            <button onClick={() => onEdit(log)} className="text-slate-500 hover:text-blue-400 transition p-1">
                                <Edit2 size={13} />
                            </button>
                            <button onClick={() => onDelete(log.id)} className="text-slate-500 hover:text-red-400 transition p-1">
                                <Trash2 size={13} />
                            </button>
                        </div>
                    </div>

                    {/* Category badges */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {log.business_type && (
                            <span className="text-[10px] bg-blue-600/15 text-blue-300 px-2 py-0.5 rounded-lg">{log.business_type}</span>
                        )}
                        {subcategory && (
                            <span className="text-[10px] bg-slate-700 text-slate-300 px-2 py-0.5 rounded-lg">{subcategory}</span>
                        )}
                        {hasBooking && (
                            <span className="text-[10px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-lg">Booking</span>
                        )}
                    </div>

                    {/* Notes preview */}
                    {notes && (
                        <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{notes}</p>
                    )}

                    {/* Status / interest row */}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-lg font-medium ${STATUS_COLORS[log.call_status] || 'bg-slate-700 text-slate-400'}`}>
                            {log.call_status?.replace(/_/g, ' ')}
                        </span>
                        {log.customer_interest_level && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-lg font-medium ${INTEREST_COLORS[log.customer_interest_level] || ''}`}>
                                {log.customer_interest_level}
                            </span>
                        )}
                        {log.needs_follow_up && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-lg font-medium ${log.priority ? PRIORITY_COLORS[log.priority] : 'bg-orange-500/15 text-orange-400'}`}>
                                ↺ Follow-up{log.priority ? ` · ${log.priority}` : ''}
                            </span>
                        )}
                        {referredUser && (
                            <span className="text-[10px] bg-indigo-500/15 text-indigo-300 px-2 py-0.5 rounded-lg">
                                → {referredUser.name}
                            </span>
                        )}
                    </div>

                    {/* Footer: logged by + date + actions */}
                    <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-white/5">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-500">by {log.logged_by || '?'}</span>
                            {log.follow_up_date && (
                                <span className="text-[10px] text-orange-400">· Due {new Date(log.follow_up_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-600">
                                {new Date(log.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </span>
                            {log.caller_number && (
                                <>
                                    <a
                                        href={`tel:${log.caller_number}`}
                                        className="text-slate-500 hover:text-green-400 transition"
                                        title="Call"
                                    >
                                        <Phone size={13} />
                                    </a>
                                    <a
                                        href={`https://wa.me/91${log.caller_number.replace(/\D/g, '').slice(-10)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-slate-500 hover:text-emerald-400 transition"
                                        title="WhatsApp"
                                    >
                                        <MessageCircle size={13} />
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ─── Main CallLogs page ────────────────────────────────────────────────────────
export default function CallLogs() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()
    const [modal, setModal] = useState(null)      // null | 'new' | log object
    const [search, setSearch] = useState('')
    const [chipFilter, setChipFilter] = useState('ALL') // ALL | FOLLOW_UP | URGENT | CONVERTED
    const [staffFilter, setStaffFilter] = useState('')
    const [sortAsc, setSortAsc] = useState(false)

    const { data: logs = [], isLoading } = useQuery({
        queryKey: ['call-logs'],
        queryFn: () => getCallLogs(),
    })
    const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: getUsers })
    const { data: customers = [] } = useQuery({ queryKey: ['customers-all'], queryFn: () => getCustomers('') })

    const createMut = useMutation({
        mutationFn: createCallLog,
        onSuccess: () => { qc.invalidateQueries(['call-logs']); setModal(null); toast.success('Call log saved') },
        onError: e => toast.error(e.message),
    })
    const updateMut = useMutation({
        mutationFn: ({ id, ...rest }) => updateCallLog(id, rest),
        onSuccess: () => { qc.invalidateQueries(['call-logs']); setModal(null); toast.success('Updated') },
        onError: e => toast.error(e.message),
    })
    const deleteMut = useMutation({
        mutationFn: deleteCallLog,
        onSuccess: () => { qc.invalidateQueries(['call-logs']); toast.success('Deleted') },
        onError: e => toast.error(e.message),
    })

    const handleSave = (data) => {
        if (modal === 'new') createMut.mutate(data)
        else updateMut.mutate({ id: modal.id, ...data })
    }

    const handleDelete = (id) => {
        if (window.confirm('Delete this call log?')) deleteMut.mutate(id)
    }

    const filtered = logs
        .filter(l => {
            const matchSearch = !search ||
                l.caller_name?.toLowerCase().includes(search.toLowerCase()) ||
                l.caller_number?.includes(search) ||
                l.caller_location?.toLowerCase().includes(search.toLowerCase())
            const matchStaff = !staffFilter || l.logged_by_id === staffFilter
            const matchChip =
                chipFilter === 'ALL' ? true :
                    chipFilter === 'FOLLOW_UP' ? l.needs_follow_up :
                        chipFilter === 'URGENT' ? l.priority === 'URGENT' :
                            chipFilter === 'CONVERTED' ? l.call_status === 'CONVERTED' :
                                true
            return matchSearch && matchStaff && matchChip
        })
        .sort((a, b) => {
            const da = new Date(a.created_at), db = new Date(b.created_at)
            return sortAsc ? da - db : db - da
        })

    const chips = [
        { key: 'ALL', label: `All (${logs.length})` },
        { key: 'FOLLOW_UP', label: `Follow-up (${logs.filter(l => l.needs_follow_up).length})` },
        { key: 'URGENT', label: `Urgent (${logs.filter(l => l.priority === 'URGENT').length})` },
        { key: 'CONVERTED', label: `Converted (${logs.filter(l => l.call_status === 'CONVERTED').length})` },
    ]

    return (
        <StaffLayout>
            <div className="space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">
                        Call Logs
                        <span className="text-slate-500 font-normal text-base ml-2">({filtered.length})</span>
                    </h1>
                    <button
                        onClick={() => setModal('new')}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
                    >
                        <Plus size={16} /> Log Call
                    </button>
                </div>

                {/* Filter chips */}
                <div className="flex flex-wrap gap-2">
                    {chips.map(c => (
                        <button
                            key={c.key}
                            onClick={() => setChipFilter(c.key)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition ${chipFilter === c.key
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-900 border border-white/5 text-slate-400 hover:text-white'
                                }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                {/* Search + staff filter + sort */}
                <div className="flex flex-wrap gap-3">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search name, phone, location..."
                            className="w-full bg-slate-900 border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={staffFilter}
                        onChange={e => setStaffFilter(e.target.value)}
                        className="bg-slate-900 border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Staff</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <button
                        onClick={() => setSortAsc(!sortAsc)}
                        className="flex items-center gap-1.5 bg-slate-900 border border-white/5 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:text-white transition"
                    >
                        <ArrowUpDown size={14} />
                        {sortAsc ? 'Oldest' : 'Newest'}
                    </button>
                </div>

                {/* Log cards */}
                {isLoading ? (
                    <div className="space-y-3">
                        {Array(4).fill(0).map((_, i) => (
                            <div key={i} className="bg-slate-900 rounded-xl border border-white/5 p-4 animate-pulse h-28" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-12 text-center text-slate-500">
                        No call logs found
                    </div>
                ) : (
                    <StaggerContainer className="space-y-3">
                        {filtered.map(log => (
                            <StaggerItem key={log.id}>
                            <CallLogCard
                                log={log}
                                users={users}
                                onEdit={setModal}
                                onDelete={handleDelete}
                            />
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                )}
            </div>

            <ModalOverlay show={!!modal}>
                <CallLogModal
                    log={modal === 'new' ? null : modal}
                    users={users}
                    customers={customers}
                    profile={profile}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                    isSaving={createMut.isLoading || updateMut.isLoading}
                />
            </ModalOverlay>
        </StaffLayout>
    )
}
